import {isReactive, isReadonly, isRef, toRaw, unref} from 'vue';
import {isObject} from '@vue/shared';
import type {Store} from 'pinia';

function recursivelyCheckObjectKeys(state: Record<string, any>, childObject = false) {
    const initialState: Record<string, any> = {};
    for (const key of Object.keys(state)) {
        if (typeof state[key] !== 'function' && !isReadonly(state[key]) && (childObject ? true : isReactive(state[key]) || isRef(state[key]))) {
            const item = unref(state[key]);
            initialState[key] = isObject(item) ? recursivelyCheckObjectKeys(toRaw(item), true) : item;
        }
    }
    return initialState;
}

function recursivelyReplaceObjectKeys(this: Store, initialState: Record<string, any>, currentState?: Record<string, any>) {
    const stateToAssign = currentState ?? (this.$state as Record<string, any>);
    for (const key of Object.keys(initialState)) {
        if (isObject(initialState[key])) {
            recursivelyReplaceObjectKeys.call(this, structuredClone(initialState[key]), stateToAssign[key]);
        } else {
            stateToAssign[key] = initialState[key];
        }
    }
}

/**
 * Will allow you to reset easily your state when you need to clean it up.
 * For exemple: When you leave a page in an SPA and need to restore state to it's initial values
 *
 * Usage:
 *
 * ```ts
 * export const useMyStore = defineStore('myStore', () => {
 *   const foo = ref(0);
 *
 *   function bar() {
 *
 *   }
 *   return withCleanup({
 *       foo,
 *       bar
 *    })
 * })
 *
 * const store = useMyStore();
 * store.$cleanup();
 * ```
 */
export function withCleanup<T extends Record<string, any>>(state: T) {
    const _initialState: Record<string, any> = recursivelyCheckObjectKeys(state);
    return {
        ...state,
        $cleanup(this: Store, dispose = true) {
            recursivelyReplaceObjectKeys.call(this, _initialState);
            if (dispose) {
                this.$dispose();
            }
        },
    };
}
